// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Tithe — the post-game read. This is a perfect-information game, so "what did everybody miss" has
// an exact answer rather than an opinion: on how many turns was there a move on the board that
// ended the game there and then, and the player played something else?
//
// That is principle #9's "show what everyone missed" in the only form this game can honestly offer,
// and it is a replay of the real log through the real rules — no separate model to drift.

import {
  COFFER,
  K_MOVE,
  PH_PLAY,
  createState,
  cellName,
  genPlies,
  isKeeper,
  isOver,
  plyFrom,
  plyKind,
  plyTo,
  step,
  type State,
} from './rules';
import { tollOf } from './tolls';

export interface MissedWin {
  seat: number;
  turn: number;
  from: number;
  to: number;
  toll: string;
  kind: 'seize' | 'collect';
}

export interface Analysis {
  /** Missed wins per seat. */
  missed: [number, number];
  first: MissedWin | null;
}

/** Does this ply end the game immediately? The same test the bot's move ordering uses. */
export function winningPly(s: State, ply: number): 'seize' | 'collect' | null {
  if (plyKind(ply) !== K_MOVE) return null;
  const to = plyTo(ply);
  const victim = s.occ[to];
  if (victim >= 0 && isKeeper(victim)) return 'seize';
  const mover = s.occ[plyFrom(ply)];
  if (mover >= 0 && isKeeper(mover) && to === COFFER[1 - s.turn]) return 'collect';
  return null;
}

export function analyse(modeId: string, seed: number, log: readonly number[]): Analysis {
  const s = createState(modeId, seed);
  const missed: [number, number] = [0, 0];
  let first: MissedWin | null = null;
  let turn = 0;

  for (const raw of log) {
    if (isOver(s)) break;
    const ply = raw >>> 0;
    if (s.phase === PH_PLAY) {
      turn++;
      const played = winningPly(s, ply);
      if (played === null) {
        const seat = s.turn;
        for (const alt of genPlies(s)) {
          const kind = winningPly(s, alt);
          if (kind === null) continue;
          missed[seat]++;
          if (first === null) {
            const toll = s.hand[seat * 2 + ((alt >>> 10) & 1)];
            first = { seat, turn, from: plyFrom(alt), to: plyTo(alt), toll: tollOf(toll).name, kind };
          }
          break; // one missed win per turn, not one per way of achieving it
        }
      }
    }
    step(s, ply);
  }
  return { missed, first };
}

export function missedNote(a: Analysis, names: [string, string]): string {
  if (a.first === null) return 'Neither of you left a win on the board.';
  const f = a.first;
  const verb = f.kind === 'seize' ? 'taken the Keeper' : 'walked into the coffer';
  return `On turn ${f.turn}, ${names[f.seat]} could have ${verb} with ${f.toll}: ${cellName(f.from)}→${cellName(f.to)}.`;
}
